import styled, { keyframes } from "styled-components";
import { White, Black, White05, OffWhite, Grey, DarkGrey } from "./colors";

// Pulse glow animation for quiz icons - white glow for contrast
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 12px rgba(255, 255, 255, 0.3),
      0 0 24px rgba(255, 255, 255, 0.15),
      inset 0 0 8px rgba(255, 255, 255, 0.1);
    transform: scale(1);
  }
  50% {
    box-shadow: 
      0 0 20px rgba(255, 255, 255, 0.5),
      0 0 40px rgba(255, 255, 255, 0.25),
      inset 0 0 12px rgba(255, 255, 255, 0.15);
    transform: scale(1.03);
  }
`;

// Float animation for icons
const floatIcon = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-2px) rotate(2deg);
  }
`;

// Helper to convert any color to rgba
const toRgba = (color, alpha) => {
  if (!color) return null;
  // Handle rgb() format
  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  }
  if (color.startsWith("rgba(")) {
    return color.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
  }
  // Handle hex format
  const hex = color.replace("#", "");
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
};

const HomeQuizBox = styled.div`
  height: 250px;
  width: 350px;
  border-radius: 30px;
  cursor: pointer;
  &:hover {
    opacity: ${(props) => (props.$selected ? "1" : "0.9")};
  }
`;

export default HomeQuizBox;

export const HomeQuizBoxTitle = styled.div`
  background: ${(props) =>
    `linear-gradient(120deg, ${props.$background || DarkGrey}, ${
      props.$transitionBackground || Black
    })`};
  color: ${White};
  border-radius: 30px 30px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;
  height: 140px;
  width: 100%;
`;

export const HomeQuizDescContainer = styled.div`
  padding: 0 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 30px 30px;
  text-align: center;
  height: 100px;
  width: 100%;
  background: ${DarkGrey};
`;

export const HomeQuizBoxIconContainer = styled.div`
  /* Outer ring - white/light glow for visibility against any background */
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.18),
    rgba(255, 255, 255, 0.06)
  );
  height: 56px;
  width: 56px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  animation: ${pulseGlow} 3s ease-in-out infinite;

  /* Inner dark circle */
  &::before {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: ${DarkGrey};
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4);
  }

  /* Icon styling - colored glow stays on the icon */
  svg {
    position: relative;
    z-index: 1;
    animation: ${floatIcon} 4s ease-in-out infinite;
    color: ${White};
    filter: drop-shadow(
      0 0 8px
        ${(props) =>
          toRgba(props.$background, 0.8) || "rgba(255, 255, 255, 0.6)"}
    );
  }
`;

export const HomeQuizBoxSm = styled.div`
  height: 160px;
  width: 100%;
  margin-bottom: 20px;
  background: ${(props) => props.$background || DarkGrey};
  color: ${OffWhite};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;
  border-radius: 5px;
  cursor: pointer;
  padding: 0 20px;
  &:hover {
    opacity: ${(props) => (props.$selected ? "1" : "0.8")};
  }
`;

export const HomeQuizBoxXs = styled.div`
  height: 90px;
  width: 100%;
  margin-bottom: 10px;
  background: ${(props) => props.$background || DarkGrey};
  color: ${OffWhite};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;
  border-radius: 5px;
  cursor: pointer;
  padding: 0 20px;
  &:hover {
    opacity: ${(props) => (props.$selected ? "1" : "0.8")};
  }
`;

export const LandingPageBox = styled.div`
  width: 100%;
  margin-bottom: 10px;
  padding: 30px 25px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 5px;
  cursor: pointer;
  color: ${White};
`;

export const ResultsPageBox = styled.div`
  width: 100%;
  margin-bottom: 10px;
  padding: 10px 40px;
  display: flex;
  color: ${OffWhite};
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 5px;
  cursor: pointer;
`;

export const QuestionContainer = styled.div`
  width: 100%;
  height: auto;
  border-radius: 0 0 5px 5px !important;
`;

export const QuestionHeader = styled.div`
  text-align: center;
  padding: 10px 15px 0px;
  border-radius: 5px 5px 0 0;
  width: 100%;
  color: ${Grey};
  min-height: 30px;
`;

export const QuestionBody = styled.div`
  padding: 0px 30px 10px;
  width: 100%;
  border-radius: 0 0 5px 5px !important;
  color: ${OffWhite};
`;

export const GradientBorderDiv = styled.div`
  padding: 1px; /* This creates the border effect */
  background: linear-gradient(90deg, #ff6ec4, #7873f5); /* your gradient */
  border-radius: 8px; /* optional, if you want rounded corners */
`;

export const QuizAnswerBox = styled.div`
  min-height: 50px;
  width: 100%;
  margin-bottom: 15px;
  background: ${DarkGrey};
  color: ${OffWhite};
  display: flex;
  justify-content: center;
  padding: 15px 25px;
  align-items: center;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.5s ease, background-color 0.5s ease;

  position: relative;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: 8px;
    background: ${(props) =>
      `linear-gradient(120deg, ${props.$background || DarkGrey}, ${
        props.$transitionBackground || DarkGrey
      })`};
    -webkit-mask: ${(props) =>
          `linear-gradient(120deg, ${props.$background || DarkGrey}, ${
            props.$transitionBackground || DarkGrey
          })`}
        content-box,
      ${(props) =>
        `linear-gradient(120deg, ${props.$background || DarkGrey}, ${
          props.$transitionBackground || DarkGrey
        })`};
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0; /* default */
    transition: opacity 0.2s ease-in-out;
    z-index: -1;
  }

  &:hover::before,
  &[data-selected="true"]::before {
    opacity: 1;
  }
`;

export const ProgressBarContainer = styled.div`
  background: ${DarkGrey};
  height: 20px;
  margin-top: 7.5px;
  margin-bottom: 15px;
`;

export const ProgressBarProgress = styled.div`
  background: ${(props) =>
    `linear-gradient(120deg, ${props.$background}, ${props.$transitionBackground})`};
  width: ${(props) => props.width || "0"};
  height: 100%;
  border-radius: 0 20px 20px 0;
`;

export const BreakdownContainer = styled.div`
  width: 95%;
  height: auto;
  margin: 20px auto 15px;
  color: ${OffWhite};
`;

export const BreakdownBody = styled.div`
  min-height: 300px;
  padding: 20px 30px;
  width: 100%;
  background-color: ${DarkGrey};
  border-radius: 30px;
`;

export const BreakdownBottom = styled.div`
  padding: 20px 30px;
  width: 100%;
  background-color: ${DarkGrey};
  border-radius: 30px;
`;

export const ResultsBox = styled.div`
  text-align: left;
  width: 100%;
  color: ${White};
  margin: 7px 15px 0;
`;

export const OverallCompatabilityCircle = styled.div`
  position: relative;
  height: 100px;
  width: 100px;
  font-size: 20px;
  border-radius: 50%;
  background-color: ${DarkGrey};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  z-index: 1;
  overflow: hidden;
  margin: 0px 20px 0;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 5px;
    border-radius: 50%;
    background: ${(props) => {
      if (props.gradientColors) {
        // Create a conic-gradient that transitions through the gradient colors within the filled portion
        const { color1, color2 } = props.gradientColors;
        return `conic-gradient(${color1} 0%, ${color2} ${
          props.percent / 2
        }%, ${color1} ${props.percent}%, ${props.bgColor} ${
          props.percent
        }% 100%)`;
      }
      return `conic-gradient(${props.fillColor} 0% ${props.percent}%, ${props.bgColor} ${props.percent}% 100%)`;
    }};
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: -1;
  }
`;

export const BlurredBackground = styled.div`
  filter: blur(3px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const BlurredOverlay = styled.div`
  color: black;
  font-size: 20px;
  font-weight: bold;
  padding: 10px 15px;
  background-color: ${Black};
  border-radius: 5px;
  cursor: pointer;
  z-index: 1; /* Ensure it's on top of the blurred content */

  &:hover {
    background-color: ${White05};
  }
`;

export const QuizBoxContainer = styled.div`
  margin-top: 40px;
  padding: 10px 0 10px 40px;
  display: flex;
  justify-content: center;
`;

export const SeeAnswersContainer = styled.div`
  width: 95%;
  height: auto;
  margin: 20px auto 30px;
  color: ${OffWhite};
`;

export const SeeAnswersBody = styled.div`
  min-height: 300px;
  padding: 30px 0;
  width: 100%;
  background-color: ${DarkGrey};
  border-radius: 30px;
  text-align: center;
  width: 100%;
  min-height: 30px;
  margin-bottom: 30px;
`;

export const InsightsContainer = styled.div`
  width: 95%;
  height: auto;
  margin: 15px auto 0px;
  color: ${OffWhite};
`;

export const InsightsBody = styled.div`
  padding: 25px 25px;
  width: 100%;
  background-color: ${DarkGrey};
  border-radius: 30px;
  font-weight: 400;
  font-size: 15px;
`;

export const UpgradeToProBox = styled.div`
  min-height: 100px;
  padding: 30px 20px;
  width: 100%;
  background-color: ${DarkGrey};
  border-radius: 30px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const CircularLockButton = styled.button`
  position: relative;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: ${(props) =>
    `linear-gradient(120deg, ${props.$background || DarkGrey}, ${
      props.$transitionBackground || DarkGrey
    })`};
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${White05};
  transition: opacity 0.2s ease;
  padding: 3px;

  &:hover {
    opacity: 0.9;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background-color: ${DarkGrey};
    z-index: 0;
  }

  svg {
    position: relative;
    z-index: 1;
  }
`;
